<template>
  <v-container fluid>
    <v-card class="mb-5">
      <v-card-title>
        Management Scoreboard
        <v-spacer></v-spacer>
        <v-btn-toggle v-model="view" mandatory dense class="mr-4">
          <v-btn small value="scoreboard">
            <v-icon left small>mdi-view-dashboard</v-icon>Scoreboard
          </v-btn>
          <v-btn small value="history">
            <v-icon left small>mdi-table</v-icon>History
          </v-btn>
        </v-btn-toggle>
        <v-text-field
          v-if="view === 'scoreboard'"
          v-model="month"
          label="Month"
          type="month"
          style="max-width: 200px"
          hide-details
          dense
        ></v-text-field>
      </v-card-title>
      <v-card-subtitle v-if="view === 'scoreboard'">
        Actual vs. target for each division this month. Green = meeting
        expectations, amber = at risk, red = behind. File review and client
        contact are current quality scores (snapshot, not month-scoped). Overdue
        and upcoming (next 90 days) deadlines are the team attorney's counts;
        lower is better, so green means at or under the goal.
      </v-card-subtitle>
      <v-card-subtitle v-else>
        Six-month history per division. Demands, settlements, fees and reviews
        are actuals for each month; file review, client contact and deadlines
        are monthly snapshots (blank for months not yet captured).
      </v-card-subtitle>
    </v-card>

    <template v-if="view === 'scoreboard'">
      <div
        v-if="$apollo.queries.divisionScoreboard.loading"
        class="text-center my-10"
      >
        <v-progress-circular indeterminate color="primary" size="64" />
      </div>

      <div
        v-else-if="!divisionScoreboard || divisionScoreboard.length === 0"
        class="text-center grey--text my-10"
      >
        No divisions found. Create divisions and assign teams to see the
        scoreboard.
      </div>

      <v-expansion-panels v-else v-model="openPanels" multiple>
        <v-expansion-panel
          v-for="division in divisionScoreboard"
          :key="division.id"
        >
          <v-expansion-panel-header>
            <v-row align="center" no-gutters>
              <v-col cols="12" sm="4">
                <v-icon
                  :color="statusColor(division.status.overall)"
                  class="mr-2"
                >
                  {{ statusIcon(division.status.overall) }}
                </v-icon>
                <span class="subtitle-1 font-weight-medium">{{
                  division.name
                }}</span>
                <span class="caption grey--text ml-2" v-if="division.manager">
                  ({{ division.manager.firstName }}
                  {{ division.manager.lastName }})
                </span>
              </v-col>
              <v-col cols="12" sm="8">
                <div class="d-flex flex-wrap metric-chips">
                  <v-chip
                    small
                    :color="statusColor(division.status.demands)"
                    dark
                  >
                    Demands {{ fmtNum(division.actual.demands) }} /
                    {{ fmtNum(division.target.demands) }}
                  </v-chip>
                  <v-chip
                    small
                    :color="statusColor(division.status.settlements)"
                    dark
                  >
                    Settlements {{ fmtNum(division.actual.settlements) }} /
                    {{ fmtNum(division.target.settlements) }}
                  </v-chip>
                  <v-chip small :color="statusColor(division.status.fees)" dark>
                    Fees {{ fmtCurrency(division.actual.fees) }} /
                    {{ fmtCurrency(division.target.fees) }}
                  </v-chip>
                  <v-chip
                    small
                    :color="statusColor(division.status.reviews)"
                    dark
                  >
                    Reviews {{ fmtNum(division.actual.reviews) }} /
                    {{ fmtNum(division.target.reviews) }}
                  </v-chip>
                  <v-chip
                    small
                    :color="statusColor(division.status.fileReview)"
                    dark
                  >
                    File Review {{ fmtPct(division.actual.fileReview) }} /
                    {{ division.target.fileReview }}%
                  </v-chip>
                  <v-chip
                    small
                    :color="statusColor(division.status.clientContact)"
                    dark
                  >
                    Client Contact {{ fmtPct(division.actual.clientContact) }} /
                    {{ division.target.clientContact }}%
                  </v-chip>
                  <v-chip
                    small
                    :color="statusColor(division.status.overdueDeadlines)"
                    dark
                  >
                    Overdue {{ fmtNum(division.actual.overdueDeadlines) }} /
                    {{ fmtNum(division.target.overdueDeadlines) }}
                  </v-chip>
                  <v-chip
                    small
                    :color="statusColor(division.status.upcomingDeadlines)"
                    dark
                  >
                    Upcoming 90d
                    {{ fmtNum(division.actual.upcomingDeadlines) }} /
                    {{ fmtNum(division.target.upcomingDeadlines) }}
                  </v-chip>
                </div>
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Team</th>
                    <th>Status</th>
                    <th>Demands</th>
                    <th>Settlements</th>
                    <th>Fees</th>
                    <th>Reviews</th>
                    <th>File Review</th>
                    <th>Client Contact</th>
                    <th>Overdue</th>
                    <th>Upcoming (90d)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!division.teams || division.teams.length === 0">
                    <td colspan="11" class="grey--text">No teams assigned.</td>
                  </tr>
                  <tr v-for="team in division.teams" :key="team.id">
                    <td>
                      {{ team.name }}
                      <span class="caption grey--text"
                        >({{ team.paralegalCount }} paralegals,
                        {{ team.targetSource }})</span
                      >
                    </td>
                    <td>
                      <v-icon small :color="statusColor(team.status.overall)">
                        {{ statusIcon(team.status.overall) }}
                      </v-icon>
                    </td>
                    <td :class="cellClass(team.status.demands)">
                      {{ fmtNum(team.actual.demands) }} /
                      {{ fmtNum(team.target.demands) }}
                    </td>
                    <td :class="cellClass(team.status.settlements)">
                      {{ fmtNum(team.actual.settlements) }} /
                      {{ fmtNum(team.target.settlements) }}
                    </td>
                    <td :class="cellClass(team.status.fees)">
                      {{ fmtCurrency(team.actual.fees) }} /
                      {{ fmtCurrency(team.target.fees) }}
                    </td>
                    <td :class="cellClass(team.status.reviews)">
                      {{ fmtNum(team.actual.reviews) }} /
                      {{ fmtNum(team.target.reviews) }}
                    </td>
                    <td :class="cellClass(team.status.fileReview)">
                      {{ fmtPct(team.actual.fileReview) }} /
                      {{ team.target.fileReview }}%
                    </td>
                    <td :class="cellClass(team.status.clientContact)">
                      {{ fmtPct(team.actual.clientContact) }} /
                      {{ team.target.clientContact }}%
                    </td>
                    <td :class="cellClass(team.status.overdueDeadlines)">
                      {{ fmtNum(team.actual.overdueDeadlines) }} /
                      {{ fmtNum(team.target.overdueDeadlines) }}
                    </td>
                    <td :class="cellClass(team.status.upcomingDeadlines)">
                      {{ fmtNum(team.actual.upcomingDeadlines) }} /
                      {{ fmtNum(team.target.upcomingDeadlines) }}
                    </td>
                    <td class="text-right">
                      <v-btn
                        x-small
                        text
                        color="primary"
                        @click="openPeople(team)"
                        >People</v-btn
                      >
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>

    <!-- History view -->
    <template v-else>
      <div
        v-if="$apollo.queries.divisionHistory.loading"
        class="text-center my-10"
      >
        <v-progress-circular indeterminate color="primary" size="64" />
      </div>

      <div
        v-else-if="!divisionHistory || divisionHistory.length === 0"
        class="text-center grey--text my-10"
      >
        No divisions found. Create divisions and assign teams to see history.
      </div>

      <v-card
        v-else
        v-for="division in divisionHistory"
        :key="division.id"
        class="mb-5"
      >
        <v-card-title class="subtitle-1">{{ division.name }}</v-card-title>
        <v-simple-table dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th>Metric</th>
                <th class="text-right">Goal</th>
                <th
                  v-for="(label, idx) in division.months"
                  :key="idx"
                  class="text-right"
                >
                  {{ label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in division.rows" :key="row.key">
                <td>{{ row.label }}</td>
                <td class="text-right font-weight-medium">
                  {{
                    row.goal !== null && row.goal !== undefined
                      ? fmtByFormat(row.goal, row.format)
                      : "—"
                  }}
                </td>
                <td
                  v-for="(value, idx) in row.values"
                  :key="idx"
                  class="text-right"
                  :class="historyCell(row, value, idx)"
                >
                  {{ fmtByFormat(value, row.format) }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card>
    </template>

    <!-- Person drill-down dialog -->
    <v-dialog v-model="peopleDialog" max-width="800px" scrollable>
      <v-card v-if="peopleTeam">
        <v-card-title>
          {{ peopleTeam.name }} — People
          <v-spacer></v-spacer>
          <v-btn icon @click="peopleDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle>
          Demands/settlements/fees are month-to-date vs. each role's target.
          File review and client contact are current quality scores (goal
          {{ defaults.fileReviewGoal }}% / {{ defaults.clientContactGoal }}%)
          and reflect today's case standing, not the selected month.
        </v-card-subtitle>
        <v-card-text>
          <div v-if="peopleLoading" class="text-center my-6">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <v-simple-table v-else dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Role</th>
                  <th>Demands</th>
                  <th>Settlements</th>
                  <th>Fees</th>
                  <th>Reviews</th>
                  <th>File Review</th>
                  <th>Client Contact</th>
                  <th>Overdue</th>
                  <th>Upcoming (90d)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="person in peopleRows" :key="person.staffCode">
                  <td>{{ person.name }}</td>
                  <td class="text-capitalize">{{ roleLabel(person.role) }}</td>
                  <td
                    :class="
                      metricCell(
                        person.demands,
                        targetFor(person.role, 'demands'),
                        person.role
                      )
                    "
                  >
                    {{ fmtNum(person.demands) }}
                    <span
                      class="caption"
                      v-if="targetFor(person.role, 'demands') > 0"
                      >/ {{ targetFor(person.role, "demands") }}</span
                    >
                  </td>
                  <td
                    :class="
                      metricCell(
                        person.settlements,
                        targetFor(person.role, 'settlements'),
                        person.role
                      )
                    "
                  >
                    {{ fmtNum(person.settlements) }}
                    <span
                      class="caption"
                      v-if="targetFor(person.role, 'settlements') > 0"
                      >/ {{ targetFor(person.role, "settlements") }}</span
                    >
                  </td>
                  <td
                    :class="
                      metricCell(
                        person.fees,
                        targetFor(person.role, 'fees'),
                        person.role
                      )
                    "
                  >
                    {{ fmtCurrency(person.fees) }}
                    <span
                      class="caption"
                      v-if="targetFor(person.role, 'fees') > 0"
                      >/ {{ fmtCurrency(targetFor(person.role, "fees")) }}</span
                    >
                  </td>
                  <td
                    :class="
                      metricCell(
                        person.reviews,
                        targetFor(person.role, 'reviews'),
                        person.role
                      )
                    "
                  >
                    {{ fmtNum(person.reviews) }}
                    <span
                      class="caption"
                      v-if="targetFor(person.role, 'reviews') > 0"
                      >/ {{ targetFor(person.role, "reviews") }}</span
                    >
                  </td>
                  <td
                    :class="
                      scoreCell(person.fileReview, defaults.fileReviewGoal)
                    "
                  >
                    <template v-if="person.fileReview !== null">
                      {{ fmtPct(person.fileReview) }}
                      <span class="caption"
                        >/ {{ defaults.fileReviewGoal }}%</span
                      >
                    </template>
                    <span v-else class="grey--text">—</span>
                  </td>
                  <td
                    :class="
                      scoreCell(
                        person.clientContact,
                        defaults.clientContactGoal
                      )
                    "
                  >
                    <template v-if="person.clientContact !== null">
                      {{ fmtPct(person.clientContact) }}
                      <span class="caption"
                        >/ {{ defaults.clientContactGoal }}%</span
                      >
                    </template>
                    <span v-else class="grey--text">—</span>
                  </td>
                  <td
                    :class="
                      ceilingCell(
                        person.overdueDeadlines,
                        defaults.overdueDeadlinesGoal
                      )
                    "
                  >
                    <template v-if="person.overdueDeadlines !== null">
                      {{ fmtNum(person.overdueDeadlines) }}
                      <span class="caption"
                        >/ {{ defaults.overdueDeadlinesGoal }}</span
                      >
                    </template>
                    <span v-else class="grey--text">—</span>
                  </td>
                  <td
                    :class="
                      ceilingCell(
                        person.upcomingDeadlines,
                        defaults.upcomingDeadlinesGoal
                      )
                    "
                  >
                    <template v-if="person.upcomingDeadlines !== null">
                      {{ fmtNum(person.upcomingDeadlines) }}
                      <span class="caption"
                        >/ {{ defaults.upcomingDeadlinesGoal }}</span
                      >
                    </template>
                    <span v-else class="grey--text">—</span>
                  </td>
                </tr>
                <tr v-if="peopleRows.length === 0">
                  <td colspan="10" class="grey--text">
                    No members on this team.
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import gql from "graphql-tag";

export default {
  name: "DashManagement",
  data() {
    const now = new Date();
    return {
      month: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
        2,
        "0"
      )}`,
      view: "scoreboard",
      divisionScoreboard: [],
      openPanels: [],
      divisionHistory: [],
      organization: null,
      defaults: {
        perParalegalDemands: 6,
        perParalegalSettlements: 5,
        perParalegalFees: 60000,
        perNegotiatorDemands: 0,
        perNegotiatorSettlements: 0,
        perNegotiatorFees: 0,
        perLegalAssistantDemands: 0,
        perLegalAssistantSettlements: 0,
        perLegalAssistantFees: 0,
        perParalegalReviews: 2,
        perNegotiatorReviews: 0,
        perLegalAssistantReviews: 0,
        fileReviewGoal: 90,
        clientContactGoal: 90,
        overdueDeadlinesGoal: 0,
        upcomingDeadlinesGoal: 10
      },
      // Person drill-down
      peopleDialog: false,
      peopleTeam: null,
      peopleLoading: false,
      demandsRaw: [],
      settlementsRaw: [],
      feesRaw: [],
      fileReviewRaw: [],
      clientContactRaw: [],
      reviewCountsRaw: []
    };
  },
  computed: {
    monthStart() {
      return `${this.month}-01`;
    },
    // Fraction of the selected month elapsed: past months = 1, current month =
    // day/daysInMonth, future months = 0. Used so person-level cells are judged
    // against where we are in the month, matching the backend scoreboard.
    monthProration() {
      const [year, m] = this.month.split("-").map(Number);
      const now = new Date();
      const curYear = now.getFullYear();
      const curMonth = now.getMonth() + 1;
      if (year < curYear || (year === curYear && m < curMonth)) return 1;
      if (year > curYear || (year === curYear && m > curMonth)) return 0;
      const daysInMonth = new Date(year, m, 0).getDate();
      return now.getDate() / daysInMonth;
    },
    // Per-role monthly targets, keyed by the stored team_role value.
    roleDefaults() {
      const d = this.defaults;
      return {
        paralegal: {
          demands: d.perParalegalDemands,
          settlements: d.perParalegalSettlements,
          fees: d.perParalegalFees,
          reviews: d.perParalegalReviews
        },
        negotiator: {
          demands: d.perNegotiatorDemands,
          settlements: d.perNegotiatorSettlements,
          fees: d.perNegotiatorFees,
          reviews: d.perNegotiatorReviews
        },
        legal_assistant: {
          demands: d.perLegalAssistantDemands,
          settlements: d.perLegalAssistantSettlements,
          fees: d.perLegalAssistantFees,
          reviews: d.perLegalAssistantReviews
        }
      };
    },
    // Fraction of the *current* calendar month elapsed; used to pace-color the
    // current-month column of the history table.
    currentMonthProration() {
      const now = new Date();
      const daysInMonth = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0
      ).getDate();
      return now.getDate() / daysInMonth;
    },
    monthEnd() {
      const [year, m] = this.month.split("-").map(Number);
      const last = new Date(year, m, 0);
      return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(last.getDate()).padStart(2, "0")}`;
    },
    teamMembersById() {
      const map = {};
      if (this.organization && this.organization.teams) {
        this.organization.teams.forEach(team => {
          map[team.id] = team.members || [];
        });
      }
      return map;
    },
    peopleRows() {
      if (!this.peopleTeam) return [];
      const members = this.teamMembersById[this.peopleTeam.id] || [];
      return members
        .map(member => {
          const code = member.user.staffCode;
          return {
            staffCode: code,
            name: `${member.user.firstName} ${member.user.lastName}`,
            role: member.role || "paralegal",
            demands: sumFor(this.demandsRaw, code),
            settlements: sumFor(this.settlementsRaw, code),
            fees: sumFor(this.feesRaw, code),
            reviews: reviewsFor(this.reviewCountsRaw, member.user.id),
            fileReview: scoreFor(this.fileReviewRaw, code, "fileReviewScore"),
            clientContact: scoreFor(
              this.clientContactRaw,
              code,
              "contactScore"
            ),
            // Deadlines are tracked at the attorney/team level; the attorney row
            // shows the team's overdue and upcoming (next 90 days) counts.
            overdueDeadlines:
              (member.role || "paralegal") === "attorney" && this.peopleTeam
                ? this.peopleTeam.actual.overdueDeadlines
                : null,
            upcomingDeadlines:
              (member.role || "paralegal") === "attorney" && this.peopleTeam
                ? this.peopleTeam.actual.upcomingDeadlines
                : null
          };
        })
        .sort((a, b) => {
          if (a.role === b.role) return a.name.localeCompare(b.name);
          return a.role === "attorney" ? -1 : 1;
        });
    }
  },
  apollo: {
    divisionScoreboard: {
      query: gql`
        query($month: String!) {
          divisionScoreboard(month: $month) {
            id
            name
            manager {
              id
              firstName
              lastName
            }
            actual {
              demands
              settlements
              fees
              reviews
              fileReview
              clientContact
              overdueDeadlines
              upcomingDeadlines
            }
            target {
              demands
              settlements
              fees
              reviews
              fileReview
              clientContact
              overdueDeadlines
              upcomingDeadlines
            }
            status {
              demands
              settlements
              fees
              reviews
              fileReview
              clientContact
              overdueDeadlines
              upcomingDeadlines
              overall
            }
            teams {
              id
              name
              paralegalCount
              targetSource
              actual {
                demands
                settlements
                fees
                reviews
                fileReview
                clientContact
                overdueDeadlines
                upcomingDeadlines
              }
              target {
                demands
                settlements
                fees
                reviews
                fileReview
                clientContact
                overdueDeadlines
                upcomingDeadlines
              }
              status {
                demands
                settlements
                fees
                reviews
                fileReview
                clientContact
                overdueDeadlines
                upcomingDeadlines
                overall
              }
            }
          }
        }
      `,
      variables() {
        return { month: this.monthStart };
      },
      fetchPolicy: "network-only",
      result({ data }) {
        const divisions = (data && data.divisionScoreboard) || [];
        this.openPanels = divisions.map((_, idx) => idx);
      }
    },
    divisionHistory: {
      query: gql`
        query($months: Int) {
          divisionHistory(months: $months) {
            id
            name
            months
            rows {
              key
              label
              format
              direction
              goal
              values
            }
          }
        }
      `,
      variables() {
        return { months: 6 };
      },
      skip() {
        return this.view !== "history";
      },
      fetchPolicy: "network-only"
    },
    organization: {
      query: gql`
        query($id: ID!) {
          organization(id: $id) {
            id
            teams {
              id
              members {
                role
                user {
                  id
                  firstName
                  lastName
                  staffCode
                  saContactId
                }
              }
            }
          }
        }
      `,
      variables() {
        return { id: this.$route.params.id };
      },
      skip() {
        return !this.$route.params.id;
      }
    },
    targetDefaults: {
      query: gql`
        query {
          targetDefaults {
            perParalegalDemands
            perParalegalSettlements
            perParalegalFees
            perNegotiatorDemands
            perNegotiatorSettlements
            perNegotiatorFees
            perLegalAssistantDemands
            perLegalAssistantSettlements
            perLegalAssistantFees
            perParalegalReviews
            perNegotiatorReviews
            perLegalAssistantReviews
            fileReviewGoal
            clientContactGoal
            overdueDeadlinesGoal
            upcomingDeadlinesGoal
          }
        }
      `,
      result({ data }) {
        if (data && data.targetDefaults) {
          this.defaults = { ...this.defaults, ...data.targetDefaults };
        }
      }
    }
  },
  methods: {
    statusColor(status) {
      if (status === "meeting") return "green";
      if (status === "at_risk") return "amber darken-2";
      if (!status) return "grey";
      return "red";
    },
    statusIcon(status) {
      if (status === "meeting") return "mdi-check-circle";
      if (status === "at_risk") return "mdi-alert";
      return "mdi-close-circle";
    },
    cellClass(status) {
      if (status === "meeting") return "metric-meeting";
      if (status === "at_risk") return "metric-at-risk";
      if (!status) return "";
      return "metric-behind";
    },
    roleLabel(role) {
      if (role === "legal_assistant") return "Legal Assistant";
      return role;
    },
    // The target for a person's role/metric, or 0 if the role has no target.
    targetFor(role, metric) {
      const set = this.roleDefaults[role];
      if (!set) return 0;
      return set[metric] || 0;
    },
    metricCell(actual, target, role) {
      // Attorneys are not held to per-member targets in the person view.
      if (role === "attorney") return "";
      if (!target || target === 0) return "";
      const expected = target * this.monthProration;
      if (expected <= 0) return "metric-meeting";
      if (actual >= expected) return "metric-meeting";
      if (actual >= expected * 0.8) return "metric-at-risk";
      return "metric-behind";
    },
    // Quality scores are snapshot percentages judged directly against the goal
    // (no month proration).
    scoreCell(score, goal) {
      if (score === null || score === undefined) return "";
      if (!goal) return "metric-meeting";
      if (score >= goal) return "metric-meeting";
      if (score >= goal * 0.8) return "metric-at-risk";
      return "metric-behind";
    },
    // Ceiling metric (lower is better), e.g. deadlines. Mirrors the backend
    // ceiling_status: zero is always best, then meeting under the cap, at risk
    // modestly over, behind well over.
    ceilingCell(actual, goal) {
      if (actual === null || actual === undefined) return "";
      const g = goal || 0;
      const margin = Math.max(1, Math.round(g * 0.5));
      if (actual <= 0) return "metric-meeting";
      if (actual < g) return "metric-meeting";
      if (actual < g + margin) return "metric-at-risk";
      return "metric-behind";
    },
    fmtNum(value) {
      if (value === undefined || value === null) return "0";
      const num = typeof value === "string" ? parseFloat(value) : value;
      return Math.round(num).toLocaleString();
    },
    fmtPct(value) {
      if (value === undefined || value === null) return "—";
      const num = typeof value === "string" ? parseFloat(value) : value;
      return `${Math.round(num)}%`;
    },
    fmtCurrency(value) {
      if (value === undefined || value === null) return "$0";
      const num = typeof value === "string" ? parseFloat(value) : value;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(num);
    },
    openPeople(team) {
      this.peopleTeam = team;
      this.peopleDialog = true;
      this.loadPeople(team);
    },
    loadPeople(team) {
      const members = this.teamMembersById[team.id] || [];
      const paralegalIds = members
        .map(m => parseInt(m.user.saContactId, 10))
        .filter(id => !isNaN(id));

      // Reviews are attributed in our DB by team, so load them even when there
      // are no SmartAdvocate contact ids.
      this.loadReviewCounts(team);

      if (paralegalIds.length === 0) {
        this.demandsRaw = [];
        this.settlementsRaw = [];
        this.feesRaw = [];
        this.fileReviewRaw = [];
        this.clientContactRaw = [];
        return;
      }

      this.peopleLoading = true;
      const variables = {
        startDate: this.monthStart,
        endDate: this.monthEnd,
        paralegalIds
      };

      Promise.all([
        this.$apollo.query({
          query: gql`
            query(
              $startDate: String!
              $endDate: String!
              $paralegalIds: [Int]
            ) {
              demandsInPeriodByParalegalByMonth(
                startDate: $startDate
                endDate: $endDate
                paralegalIds: $paralegalIds
              ) {
                paralegal
                total
              }
            }
          `,
          variables,
          fetchPolicy: "network-only"
        }),
        this.$apollo.query({
          query: gql`
            query(
              $startDate: String!
              $endDate: String!
              $paralegalIds: [Int]
            ) {
              settlementsInPeriodByParalegalByMonth(
                startDate: $startDate
                endDate: $endDate
                paralegalIds: $paralegalIds
              ) {
                paralegal
                total
              }
            }
          `,
          variables,
          fetchPolicy: "network-only"
        }),
        this.$apollo.query({
          query: gql`
            query(
              $startDate: String!
              $endDate: String!
              $paralegalIds: [Int]
            ) {
              feesInPeriodByParalegalByMonth(
                startDate: $startDate
                endDate: $endDate
                paralegalIds: $paralegalIds
              ) {
                paralegal
                total
              }
            }
          `,
          variables,
          fetchPolicy: "network-only"
        }),
        this.$apollo.query({
          query: gql`
            query($ids: [Int]) {
              fileReviewScores(ids: $ids) {
                staff
                fileReviewScore
              }
            }
          `,
          variables: { ids: paralegalIds },
          fetchPolicy: "network-only"
        }),
        this.$apollo.query({
          query: gql`
            query($ids: [Int]) {
              clientContactScores(ids: $ids) {
                staff
                contactScore
              }
            }
          `,
          variables: { ids: paralegalIds },
          fetchPolicy: "network-only"
        })
      ])
        .then(([demands, settlements, fees, fileReview, clientContact]) => {
          this.demandsRaw =
            demands.data.demandsInPeriodByParalegalByMonth || [];
          this.settlementsRaw =
            settlements.data.settlementsInPeriodByParalegalByMonth || [];
          this.feesRaw = fees.data.feesInPeriodByParalegalByMonth || [];
          this.fileReviewRaw = fileReview.data.fileReviewScores || [];
          this.clientContactRaw = clientContact.data.clientContactScores || [];
        })
        .catch(error => console.error(error))
        .finally(() => {
          this.peopleLoading = false;
        });
    },
    loadReviewCounts(team) {
      this.reviewCountsRaw = [];
      this.$apollo
        .query({
          query: gql`
            query($teamId: ID!, $month: String!) {
              teamReviewCounts(teamId: $teamId, month: $month) {
                userId
                count
              }
            }
          `,
          variables: { teamId: team.id, month: this.monthStart },
          fetchPolicy: "network-only"
        })
        .then(({ data }) => {
          this.reviewCountsRaw = (data && data.teamReviewCounts) || [];
        })
        .catch(error => console.error(error));
    },
    fmtByFormat(value, format) {
      if (value === null || value === undefined) return "—";
      if (format === "currency") return this.fmtCurrency(value);
      if (format === "percent") return this.fmtPct(value);
      return this.fmtNum(value);
    },
    // Colors a history cell. Monthly metrics are paced against the goal (the
    // current month, index 0, is prorated); percent scores compare directly.
    historyCell(row, value, monthIndex) {
      if (value === null || value === undefined) return "";
      // Ceiling metrics (deadlines): lower is better, no proration since they
      // are point-in-time snapshots.
      if (row.direction === "lower") {
        return this.ceilingCell(value, row.goal);
      }
      const goal = row.goal;
      if (!goal || goal <= 0) return "";
      if (row.format === "percent") {
        if (value >= goal) return "metric-meeting";
        if (value >= goal * 0.8) return "metric-at-risk";
        return "metric-behind";
      }
      const expected =
        monthIndex === 0 ? goal * this.currentMonthProration : goal;
      if (expected <= 0) return "metric-meeting";
      if (value >= expected) return "metric-meeting";
      if (value >= expected * 0.8) return "metric-at-risk";
      return "metric-behind";
    }
  }
};

function sumFor(rows, staffCode) {
  if (!rows || !staffCode) return 0;
  return rows
    .filter(r => r.paralegal === staffCode)
    .reduce((acc, r) => acc + (parseFloat(r.total) || 0), 0);
}

// Review counts are keyed by our user id.
function reviewsFor(rows, userId) {
  if (!rows || userId === null || userId === undefined) return 0;
  const row = rows.find(r => String(r.userId) === String(userId));
  return row ? parseInt(row.count, 10) || 0 : 0;
}

// Snapshot scores are keyed by staff login id (matches user.staffCode). Returns
// null when there is no score row for that person.
function scoreFor(rows, staffCode, field) {
  if (!rows || !staffCode) return null;
  const row = rows.find(r => r.staff === staffCode);
  if (!row || row[field] === null || row[field] === undefined) return null;
  return parseFloat(row[field]);
}
</script>

<style scoped>
.metric-meeting {
  background-color: #e8f5e9;
  color: #2e7d32;
}
.metric-at-risk {
  background-color: #fff8e1;
  color: #f9a825;
}
.metric-behind {
  background-color: #ffebee;
  color: #c62828;
}
.metric-chips {
  gap: 6px;
}
.metric-chips .v-chip {
  margin: 0;
}
</style>
